import React, { SyntheticEvent } from 'react';
import { CircularProgress, useMediaQuery, useTheme } from '@material-ui/core';

import { StyledButton, StyledCustomButton } from './Button.style';
import { ICustomTheme } from 'interfaces';

export type CustomButtonType = {
  /** color used for the background */
  color?:
    | 'primary'
    | 'primaryInverted'
    | 'secondary'
    | 'secondaryInverted'
    | 'info'
    | 'infoInverted'
    | 'loading'
    | 'disabled'
    | 'success'
    | 'error';
  /** boolean used to determine if the label is bold */
  isLabelBold?: boolean;
  /** text inside the button */
  label?: string;
  /** standard children prop: accepts any valid React Node */
  children?: React.ReactNode;
  /** type of the button action */
  btnType?: 'submit' | 'reset' | 'button';
  /** type of the button action */
  isDisabled?: boolean;
  /** display loader */
  isLoading?: boolean;
  /** size of the button */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** shadow around the button */
  withShadow?: boolean;
  /** callback function passed to the onClick handler */
  onClick?: (event: SyntheticEvent) => void;
  /** Any other prop of any type */
  [key: string]: unknown;
};

export const CustomButton = ({
  color = 'primary',
  children,
  isLabelBold = true,
  isDisabled = false,
  isLoading = false,
  label,
  btnType = 'submit',
  size = 'md',
  withShadow = false,
  onClick,
  ...otherProps
}: CustomButtonType): JSX.Element => {
  const theme: ICustomTheme = useTheme();
  return (
    <StyledButton
      boxShadow={withShadow}
      textBold={isLabelBold}
      variantColor={isDisabled ? 'disabled' : color}
      variantSize={size}
      type={btnType}
      disabled={isDisabled}
      onClick={onClick}
      {...otherProps}
    >
      {isLoading ? (
        <CircularProgress
          style={{
            color: theme.colors.paletteFromBO.main,
          }}
        />
      ) : (
        children || label
      )}
    </StyledButton>
  );
};

export default CustomButton;

export type TButton = {
  /** boolean used to determine if the label is bold */
  isLabelBold?: boolean;
  /** text inside the button */
  label?: string;
  /** standard children prop: accepts any valid React Node */
  children?: React.ReactNode;
  /** type of the button action */
  btnType?: 'submit' | 'reset' | 'button';
  /** type of the button action */
  isDisabled?: boolean;
  /** display loader */
  isLoading?: boolean;
  /** size of the button */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** shadow around the button */
  withShadow?: boolean;
  /** callback function passed to the onClick handler */
  onClick?: (event: SyntheticEvent) => void;
  /** Any other prop of any type */
  [key: string]: unknown;
};

export const PrimaryButton = ({
  children,
  isLabelBold = true,
  isDisabled = false,
  isLoading = false,
  label,
  btnType = 'submit',
  size = 'md',
  withShadow = false,
  onClick,
  ...otherProps
}: TButton): JSX.Element => {
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

  return (
    <StyledCustomButton
      boxShadow={withShadow}
      textBold={isLabelBold}
      buttonColor={theme.colors.paletteFromBO.primary}
      variantSize={size}
      type={btnType}
      disabled={isDisabled}
      onClick={onClick}
      isMobile={isMobile}
      {...otherProps}
    >
      {isLoading ? (
        <CircularProgress
          style={{
            color: theme.colors.paletteFromBO.main,
          }}
        />
      ) : (
        children || label
      )}
    </StyledCustomButton>
  );
};

export const SecondaryButton = ({
  children,
  isLabelBold = true,
  isDisabled = false,
  isLoading = false,
  label,
  btnType = 'submit',
  size = 'md',
  withShadow = false,
  onClick,
  ...otherProps
}: TButton): JSX.Element => {
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  return (
    <StyledCustomButton
      boxShadow={withShadow}
      textBold={isLabelBold}
      buttonColor={theme.colors.paletteFromBO.secondary}
      variantSize={size}
      type={btnType}
      disabled={isDisabled}
      onClick={onClick}
      isMobile={isMobile}
      {...otherProps}
    >
      {isLoading ? (
        <CircularProgress
          style={{
            color: theme.colors.paletteFromBO.main,
          }}
        />
      ) : (
        children || label
      )}
    </StyledCustomButton>
  );
};

export const TertiaryButton = ({
  children,
  isLabelBold = true,
  isDisabled = false,
  isLoading = false,
  label,
  btnType = 'submit',
  size = 'md',
  withShadow = false,
  onClick,
  ...otherProps
}: TButton): JSX.Element => {
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  return (
    <StyledCustomButton
      boxShadow={withShadow}
      textBold={isLabelBold}
      buttonColor={theme.colors.paletteFromBO.tertiary}
      variantSize={size}
      type={btnType}
      disabled={isDisabled}
      onClick={onClick}
      isMobile={isMobile}
      {...otherProps}
    >
      {isLoading ? (
        <CircularProgress
          style={{
            color: theme.colors.paletteFromBO.main,
          }}
        />
      ) : (
        children || label
      )}
    </StyledCustomButton>
  );
};

export const MainButton = ({
  children,
  isLabelBold = true,
  isDisabled = false,
  isLoading = false,
  label,
  btnType = 'submit',
  size = 'md',
  withShadow = false,
  onClick,
  ...otherProps
}: TButton): JSX.Element => {
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  return (
    <StyledCustomButton
      boxShadow={withShadow}
      textBold={isLabelBold}
      buttonColor={theme.colors.paletteFromBO.main}
      variantSize={size}
      type={btnType}
      disabled={isDisabled}
      onClick={onClick}
      isMobile={isMobile}
      {...otherProps}
    >
      {isLoading ? (
        <CircularProgress
          style={{
            color: theme.colors.paletteFromBO.main,
          }}
        />
      ) : (
        children || label
      )}
    </StyledCustomButton>
  );
};
